import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import DeleteConfirmationModal from '../../../components/ui/DeleteConfirmationModal';
import EditModuleModal from './EditModuleModal';
import BulkEditModuleModal from './BulkEditModuleModal';
import projectDashboardService from '../../../services/projectDashboardService';
import moduleRealtimeService from '../../../services/moduleRealtimeService';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const SortableModuleRow = ({ module, onEdit, onDelete, onRowClick, isSelected, onSelect, formatProductionValue, formatDimensions, formatUnitType, index }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: module?.id });

  const style = {
    transform: CSS?.Transform?.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={`border-b border-border hover:bg-green-500/20 hover:shadow-[0_0_15px_rgba(34,197,94,0.3)] transition-all cursor-pointer ${
        index % 2 === 0 ? 'bg-white' : 'bg-muted/30'
      }`}
      onClick={() => onRowClick(module)}
    >
      <td className="px-3 py-2 text-center">
        <div className="flex items-center justify-center gap-2">
          <div
            {...attributes}
            {...listeners}
            className="cursor-grab active:cursor-grabbing p-1 hover:bg-muted rounded"
            onClick={(e) => e?.stopPropagation()}
          >
            <Icon name="GripVertical" size={14} color="var(--color-muted-foreground)" />
          </div>
          <input
            type="checkbox"
            checked={isSelected}
            onChange={(e) => onSelect(module?.id, e)}
            onClick={(e) => e?.stopPropagation()}
            className="w-3.5 h-3.5 rounded border-border text-primary focus:ring-primary cursor-pointer"
          />
        </div>
      </td>
      <td className="px-3 py-2 text-xs text-foreground">{module?.name || 'Unnamed Module'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.quantity || 0}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{formatProductionValue(module?.productionValueUsd)}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.pending || 0}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.onFloor || 0}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.completed || 0}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.delayed || 0}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{formatDimensions(module?.dimensionLength, module?.dimensionWidth, module?.dimensionHeight)}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{module?.weight ? `${module?.weight} Kg` : 'N/A'}</td>
      <td className="px-3 py-2 text-xs text-muted-foreground text-center">{formatUnitType(module?.dryWetUnit)}</td>
      <td className="px-3 py-2 text-center">
        <div className="flex items-center justify-center gap-1">
          <button
            onClick={(e) => onEdit(module, e)}
            className="p-1 hover:bg-muted rounded-lg transition-colors"
            title="Edit Module"
          >
            <Icon name="Edit" size={13} color="var(--color-primary)" />
          </button>
          <button
            onClick={(e) => onDelete(module, e)}
            className="p-1 hover:bg-muted rounded-lg transition-colors"
            title="Delete Module"
          >
            <Icon name="Trash2" size={13} color="var(--color-destructive)" />
          </button>
        </div>
      </td>
    </tr>
  );
};

const ModuleList = ({ modules = [], onModuleUpdated }) => {
  const navigate = useNavigate();
  const { projectId } = useParams();
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [moduleToEdit, setModuleToEdit] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState(null);
  const [selectedModules, setSelectedModules] = useState([]);
  const [showBulkEditModal, setShowBulkEditModal] = useState(false);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [orderedModules, setOrderedModules] = useState([]);

  // Initialize ordered modules when modules prop changes
  useEffect(() => {
    setOrderedModules(modules);
  }, [modules]);

  // Drag and drop sensors
  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end
  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (!over || active?.id === over?.id) {
      return;
    }

    const oldIndex = orderedModules?.findIndex((m) => m?.id === active?.id);
    const newIndex = orderedModules?.findIndex((m) => m?.id === over?.id);

    if (oldIndex === -1 || newIndex === -1) {
      return;
    }

    // Optimistically update UI
    const newOrder = arrayMove(orderedModules, oldIndex, newIndex);
    setOrderedModules(newOrder);

    // Prepare bulk update with new display orders
    const updates = newOrder?.map((module, index) => ({
      id: module?.id,
      displayOrder: index,
    }));

    // Save to database
    const { error } = await projectDashboardService?.bulkUpdateModuleOrder(updates);

    if (error) {
      console.error('Failed to update module order:', error);
      // Revert on error
      setOrderedModules(modules);
    }
  };

  // Real-time subscription for module changes
  useEffect(() => {
    if (!projectId) return;

    const channel = moduleRealtimeService?.subscribeToModuleChanges(
      projectId,
      (payload) => {
        console.log('Module change detected:', payload?.event, payload?.data);
        
        // Refresh module list when any change occurs
        if (onModuleUpdated) {
          onModuleUpdated();
        }
      }
    );

    // Cleanup subscription on unmount
    return () => {
      moduleRealtimeService?.unsubscribe(channel);
    };
  }, [projectId, onModuleUpdated]);

  // Multi-select handlers
  const handleSelectAll = (e) => {
    if (e?.target?.checked) {
      setSelectedModules(orderedModules?.map(m => m?.id));
    } else {
      setSelectedModules([]);
    }
  };

  const handleSelectModule = (moduleId, e) => {
    e?.stopPropagation();
    if (selectedModules?.includes(moduleId)) {
      setSelectedModules(selectedModules?.filter(id => id !== moduleId));
    } else {
      setSelectedModules([...selectedModules, moduleId]);
    }
  };

  const isAllSelected = orderedModules?.length > 0 && selectedModules?.length === orderedModules?.length;
  const isSomeSelected = selectedModules?.length > 0 && selectedModules?.length < orderedModules?.length;

  // Bulk action handlers
  const handleBulkEdit = () => {
    setShowBulkEditModal(true);
  };

  const handleBulkEditSubmit = async (updates) => {
    setBulkActionLoading(true);
    const { error } = await projectDashboardService?.bulkUpdateModules(selectedModules, updates);
    
    if (error) {
      console.error('Failed to bulk update modules:', error);
    } else {
      setShowBulkEditModal(false);
      setSelectedModules([]);
      if (onModuleUpdated) {
        onModuleUpdated();
      }
    }
    setBulkActionLoading(false);
  };

  const handleBulkApprove = async () => {
    setBulkActionLoading(true);
    const { error } = await projectDashboardService?.bulkApproveModules(selectedModules);
    
    if (error) {
      console.error('Failed to bulk approve modules:', error);
    } else {
      setSelectedModules([]);
      if (onModuleUpdated) {
        onModuleUpdated();
      }
    }
    setBulkActionLoading(false);
  };

  const handleBulkSchedule = async () => {
    setBulkActionLoading(true);
    const { error } = await projectDashboardService?.bulkScheduleModules(selectedModules);
    
    if (error) {
      console.error('Failed to bulk schedule modules:', error);
    } else {
      setSelectedModules([]);
      if (onModuleUpdated) {
        onModuleUpdated();
      }
    }
    setBulkActionLoading(false);
  };

  const handleEditModule = (module, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setModuleToEdit(module);
  };

  const handleUpdateModule = async (updatedData) => {
    const { data, error } = await projectDashboardService?.updateModule(updatedData);
    
    if (error) {
      console.error('Failed to update module:', error);
      return;
    }
    
    setModuleToEdit(null);
    if (onModuleUpdated) {
      onModuleUpdated();
    }
  };

  const handleRowClick = (module) => {
    navigate(`/project/${projectId}/module/${module?.id}`);
  };

  const handleDeleteModule = (module, e) => {
    e?.stopPropagation();
    e?.preventDefault();
    setDeleteError(null);
    setModuleToDelete(module);
  };

  const handleConfirmDelete = async () => {
    if (!moduleToDelete?.id) return;
    
    setIsDeleting(true);
    setDeleteError(null);
    
    try {
      const { error } = await projectDashboardService?.deleteModule(moduleToDelete?.id);
      
      if (error) {
        setDeleteError(error);
        console.error('Failed to delete module:', error);
        return;
      }
      
      setModuleToDelete(null);
      if (onModuleUpdated) {
        onModuleUpdated();
      }
    } catch (err) {
      setDeleteError(err?.message || 'Failed to delete module');
      console.error('Error deleting module:', err);
    } finally {
      setIsDeleting(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-primary)';
      case 'delayed':
        return 'var(--color-destructive)';
      case 'pending':
      default:
        return 'var(--color-warning)';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'in-progress':
        return 'In Progress';
      case 'completed':
        return 'Completed';
      case 'delayed':
        return 'Delayed';
      case 'pending':
      default:
        return 'Pending';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const formatProductionValue = (value) => {
    if (!value || value === 0) return 'N/A';
    return `$${parseFloat(value)?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} USD`;
  };

  const formatDimensions = (length, width, height) => {
    if (!length || !width || !height) return 'N/A';
    // Convert from millimeters to meters
    const lengthM = (parseFloat(length) / 1000)?.toFixed(2);
    const widthM = (parseFloat(width) / 1000)?.toFixed(2);
    const heightM = (parseFloat(height) / 1000)?.toFixed(2);
    return `${lengthM} X ${widthM} X ${heightM}`;
  };

  const formatUnitType = (unitType) => {
    if (!unitType) return 'N/A';
    return unitType?.toLowerCase() === 'dry' ? 'Dry' : unitType?.toLowerCase() === 'wet' ? 'Wet' : unitType;
  };

  return (
    <div className="bg-card rounded-xl border border-border p-5 h-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
            <Icon name="Package" size={16} color="var(--color-primary)" />
          </div>
          <h3 className="text-base font-semibold text-foreground">Module List</h3>
        </div>
        <span className="text-xs text-muted-foreground">
          {orderedModules?.length || 0} {orderedModules?.length === 1 ? 'Module' : 'Modules'}
        </span>
      </div>

      {/* Bulk Actions Toolbar */}
      {selectedModules?.length > 0 && (
        <div className="mb-3 bg-primary/10 border border-primary/20 rounded-lg p-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Icon name="CheckSquare" size={16} color="var(--color-primary)" />
            <span className="text-sm font-medium text-foreground">
              {selectedModules?.length} {selectedModules?.length === 1 ? 'module' : 'modules'} selected
            </span>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={handleBulkEdit}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Edit" size={14} />
              Edit
            </button>
            <button
              onClick={handleBulkApprove}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 rounded-lg bg-green-600 text-white hover:bg-green-700 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="CheckCircle" size={14} />
              Approve
            </button>
            <button
              onClick={handleBulkSchedule}
              disabled={bulkActionLoading}
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 transition-colors text-sm font-medium flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Icon name="Calendar" size={14} />
              Schedule
            </button>
          </div>
        </div>
      )}

      <div className="overflow-auto" style={{ maxHeight: '512px' }}>
        {orderedModules?.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            <Icon name="Package" size={48} color="var(--color-muted-foreground)" className="mx-auto mb-2 opacity-50" />
            <p className="text-sm">No modules found</p>
          </div>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0 z-10">
                <tr className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">
                  <th className="px-3 py-2 text-center w-20">
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(input) => {
                        if (input) {
                          input.indeterminate = isSomeSelected;
                        }
                      }}
                      onChange={handleSelectAll}
                      className="w-3.5 h-3.5 rounded border-border text-primary focus:ring-primary cursor-pointer"
                    />
                  </th>
                  <th className="px-3 py-2 text-left">Module Name</th>
                  <th className="px-3 py-2 text-center">Qty</th>
                  <th className="px-3 py-2 text-center">Production Value</th>
                  <th className="px-3 py-2 text-center">Pending</th>
                  <th className="px-3 py-2 text-center">On Floor</th>
                  <th className="px-3 py-2 text-center">Completed</th>
                  <th className="px-3 py-2 text-center">Delayed</th>
                  <th className="px-3 py-2 text-center">Dimensions M</th>
                  <th className="px-3 py-2 text-center">Weight</th>
                  <th className="px-3 py-2 text-center">Unit Type</th>
                  <th className="px-3 py-2 text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                <SortableContext
                  items={orderedModules?.map((m) => m?.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {orderedModules?.map((module, index) => (
                    <SortableModuleRow
                      key={module?.id}
                      module={module}
                      onEdit={handleEditModule}
                      onDelete={handleDeleteModule}
                      onRowClick={handleRowClick}
                      isSelected={selectedModules?.includes(module?.id)}
                      onSelect={handleSelectModule}
                      formatProductionValue={formatProductionValue}
                      formatDimensions={formatDimensions}
                      formatUnitType={formatUnitType}
                      index={index}
                    />
                  ))}
                </SortableContext>
              </tbody>
            </table>
          </DndContext>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={!!moduleToDelete}
        onClose={() => {
          setModuleToDelete(null);
          setDeleteError(null);
        }}
        onConfirm={handleConfirmDelete}
        title="Delete Module"
        itemName={moduleToDelete?.name || 'this module'}
        message={`Are you sure you want to delete "${moduleToDelete?.name}"? This action cannot be undone.`}
        warningMessage="This action cannot be undone."
        additionalInfo="All associated data will be permanently removed."
        isDeleting={isDeleting}
        error={deleteError}
      />

      {/* Edit Module Modal */}
      {moduleToEdit && (
        <EditModuleModal
          isOpen={!!moduleToEdit}
          onClose={() => setModuleToEdit(null)}
          onSubmit={handleUpdateModule}
          module={moduleToEdit}
        />
      )}

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <BulkEditModuleModal
          isOpen={showBulkEditModal}
          onClose={() => setShowBulkEditModal(false)}
          onSubmit={handleBulkEditSubmit}
          selectedCount={selectedModules?.length}
          isLoading={bulkActionLoading}
        />
      )}
    </div>
  );
};

export default ModuleList;